import React, { useState, useEffect, useRef, useCallback } from 'react';
import api, { extractErrorMessage } from '@app/services/api';
import { useUserSearch } from '@platform/query';
import TextEditor from '@modules/database/work-items/frontend/editors/TextEditor';
import DateEditor from '@modules/database/work-items/frontend/editors/DateEditor';
import SelectEditor from '@modules/database/work-items/frontend/editors/SelectEditor';
import StatusEditor from '@modules/database/work-items/frontend/editors/StatusEditor';
import UserEditor from '@modules/database/work-items/frontend/editors/UserEditor';
import LabelsEditor from '@modules/database/work-items/frontend/editors/LabelsEditor';
import MultiSelectEditor from '@modules/database/work-items/frontend/editors/MultiSelectEditor';
import NumberEditor from '@modules/database/work-items/frontend/editors/NumberEditor';
import TimeEditor from '@modules/database/work-items/frontend/editors/TimeEditor';
import ParentEditor from '@modules/database/work-items/frontend/editors/ParentEditor';
import TransitionScreenModal from '@modules/database/work-items/frontend/components/TransitionScreenModal';
import { combineJqlFilters, combineExpressions } from '@modules/folders/frontend/utils/edgeFilters';
import '@modules/database/work-items/frontend/editors/FieldEditor.css';

// Module-level options cache (5 min TTL)
const optionsCache = {};
const CACHE_TTL = 5 * 60 * 1000;

function getCached(key) {
  const entry = optionsCache[key];
  if (entry && Date.now() - entry.ts < CACHE_TTL) return entry.data;
  return null;
}

function setCache(key, data) {
  optionsCache[key] = { data, ts: Date.now() };
}

function FieldEditor({
  issueKey,
  fieldId,
  editorType: editorTypeProp,
  currentValue,
  anchorRect,
  issueData,
  jiraFields,
  columns,
  onSave,
  onClose,
  onSaving,
  parentEditorConfig,
}) {
  // Cached user lookup for the user-picker editor (see referenceQueries). A
  // custom user field searches within its context's allowed users (Jira's
  // group / project-role restriction); assignee & co. keep the full list.
  const searchAllUsers = useUserSearch();
  const userScopeFieldId = fieldId && String(fieldId).startsWith('customfield_') ? fieldId : null;
  const userScopeProject = issueData?.fields?.project?.key;
  const userScopeType = issueData?.fields?.issuetype?.id;
  const searchUsers = useCallback(
    (q) => searchAllUsers(q, userScopeFieldId
      ? { fieldId: userScopeFieldId, projectKey: userScopeProject, issueTypeId: userScopeType, issueKey }
      : null),
    [searchAllUsers, userScopeFieldId, userScopeProject, userScopeType, issueKey],
  );
  // Auto-correct editor type if the value is an array but type isn't multiselect
  const editorType = (Array.isArray(currentValue) && editorTypeProp !== 'multiselect')
    ? 'multiselect'
    : editorTypeProp;
  const [editValue, setEditValue] = useState(currentValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState([]);
  const [transitions, setTransitions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [savingTransitionId, setSavingTransitionId] = useState(null);
  const [screenTransition, setScreenTransition] = useState(null);
  const popoverRef = useRef(null);

  // Types that auto-save on selection (close after pick)
  const isAutoSaveOnSelect = ['select', 'user', 'date', 'status', 'parent'].includes(editorType);
  // Types that save when the editor is closed (backdrop click / x button)
  const isSaveOnClose = ['text', 'number', 'time', 'labels', 'multiselect'].includes(editorType);
  // Types that need body padding (input-style editors)
  const needsPadding = ['text', 'number', 'time'].includes(editorType);

  // Position the popover near the anchor cell
  const getPosition = () => {
    if (!anchorRect) return {};

    const popoverWidth = 320;
    const popoverHeight = 300;
    const margin = 8;

    let top = anchorRect.bottom + margin;
    let left = anchorRect.left;

    if (top + popoverHeight > window.innerHeight - margin) {
      top = anchorRect.top - popoverHeight - margin;
    }
    if (left + popoverWidth > window.innerWidth - margin) {
      left = window.innerWidth - popoverWidth - margin;
    }
    if (left < margin) left = margin;

    return { top: `${top}px`, left: `${left}px` };
  };

  // Close on Escape (always discards)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Fetch options/transitions on mount for types that need it
  useEffect(() => {
    const loadOptions = async () => {
      setOptionsLoading(true);
      try {
        if (editorType === 'status') {
          const result = await api.getTransitions(issueKey);
          setTransitions(result.transitions || []);
        } else if (editorType === 'select' && fieldId === 'priority') {
          const cacheKey = 'priorities';
          const cached = getCached(cacheKey);
          if (cached) { setOptions(cached); }
          else {
            const result = await api.getPriorities();
            setOptions(result);
            setCache(cacheKey, result);
          }
        } else if (editorType === 'select' && fieldId === 'resolution') {
          const cacheKey = 'resolutions';
          const cached = getCached(cacheKey);
          if (cached) { setOptions(cached); }
          else {
            const result = await api.getResolutions();
            setOptions(result);
            setCache(cacheKey, result);
          }
        } else if (editorType === 'select' && fieldId?.startsWith('customfield_')) {
          const projectKey = issueData?.fields?.project?.key;
          const issueTypeId = issueData?.fields?.issuetype?.id;
          const cacheKey = `field-options:${fieldId}:${projectKey}:${issueTypeId}`;
          const cached = getCached(cacheKey);
          if (cached) { setOptions(cached); }
          else {
            const result = await api.getFieldOptions(fieldId, projectKey, issueTypeId, issueKey);
            const opts = Array.isArray(result) ? result : (result?.values || result?.allowedValues || result || []);
            setOptions(opts);
            setCache(cacheKey, opts);
          }
        } else if (editorType === 'multiselect' && fieldId === 'components') {
          const projectKey = issueData?.fields?.project?.key;
          if (projectKey) {
            const cacheKey = `components:${projectKey}`;
            const cached = getCached(cacheKey);
            if (cached) { setOptions(cached); }
            else {
              const result = await api.getProjectComponents(projectKey);
              setOptions(result);
              setCache(cacheKey, result);
            }
          }
        } else if (editorType === 'multiselect' && fieldId === 'fixVersions') {
          const projectKey = issueData?.fields?.project?.key;
          if (projectKey) {
            const cacheKey = `versions:${projectKey}`;
            const cached = getCached(cacheKey);
            if (cached) { setOptions(cached); }
            else {
              const result = await api.getProjectVersions(projectKey);
              setOptions(result);
              setCache(cacheKey, result);
            }
          }
        } else if (editorType === 'multiselect' && fieldId?.startsWith('customfield_')) {
          const projectKey = issueData?.fields?.project?.key;
          const issueTypeId = issueData?.fields?.issuetype?.id;
          const cacheKey = `field-options:${fieldId}:${projectKey}:${issueTypeId}`;
          const cached = getCached(cacheKey);
          if (cached) { setOptions(cached); }
          else {
            const result = await api.getFieldOptions(fieldId, projectKey, issueTypeId, issueKey);
            const opts = Array.isArray(result) ? result : (result?.values || result?.allowedValues || result || []);
            setOptions(opts);
            setCache(cacheKey, opts);
          }
        }
      } catch (err) {
        console.error('Failed to load options:', err);
        setError('Failed to load options');
      } finally {
        setOptionsLoading(false);
      }
    };

    if (['select', 'multiselect', 'status'].includes(editorType)) {
      loadOptions();
    }
  }, [editorType, fieldId, issueKey]);

  // Format value for Jira API
  const formatForApi = (val) => {
    switch (fieldId) {
      case 'summary':
        return { summary: val || '' };
      case 'duedate':
        return { duedate: val || null };
      case 'startDate':
        return { customfield_10015: val || null };
      case 'priority':
        return { priority: val ? { id: val.id } : null };
      case 'resolution':
        return { resolution: val ? { id: val.id } : null };
      case 'assignee':
        return { assignee: val ? { accountId: val.accountId } : null };
      case 'reporter':
        return { reporter: val ? { accountId: val.accountId } : null };
      case 'labels':
        return { labels: Array.isArray(val) ? val : [] };
      case 'components':
        return { components: (Array.isArray(val) ? val : []).map(v => ({ id: v.id })) };
      case 'fixVersions':
        return { fixVersions: (Array.isArray(val) ? val : []).map(v => ({ id: v.id })) };
      case 'timeoriginalestimate':
        return { timetracking: { originalEstimate: val || '0m' } };
      case 'timeestimate':
        return { timetracking: { remainingEstimate: val || '0m' } };
      default:
        // Custom fields
        if (fieldId?.startsWith('customfield_')) {
          if (editorType === 'select') {
            return { [fieldId]: val ? { id: val.id, value: val.value || val.name } : null };
          }
          if (editorType === 'multiselect') {
            return { [fieldId]: (Array.isArray(val) ? val : []).filter(v => v != null).map(v => ({ id: v.id, value: v.value || v.name })) };
          }
          if (editorType === 'number') {
            return { [fieldId]: val === '' || val === null ? null : Number(val) };
          }
          if (editorType === 'date') {
            // Jira rejects '' on a date field with "must be yyyy-MM-dd";
            // only null clears it. (Maps both 'date' and 'datetime' Jira
            // schema types — see fieldConfig.js.)
            return { [fieldId]: val || null };
          }
          // Safety net for unknown customfields: send null on empty string.
          // Jira accepts null to clear any field; '' is rejected by some
          // (e.g. date-flavoured customfields whose schema we misclassified).
          // Both forms clear text customfields, so this is universally safe.
          return { [fieldId]: val === '' ? null : val };
        }
        return { [fieldId]: val };
    }
  };

  // Build local state update for the saved value
  const buildLocalUpdate = (val) => {
    switch (fieldId) {
      case 'summary':
        return { summary: val };
      case 'duedate':
        return { duedate: val || null };
      case 'startDate':
        return { startDate: val || null, customfield_10015: val || null };
      case 'priority':
        return val ? { priority: { id: val.id, name: val.name, iconUrl: val.iconUrl } } : { priority: null };
      case 'resolution':
        return val ? { resolution: { id: val.id, name: val.name } } : { resolution: null };
      case 'assignee':
        return val ? { assignee: { accountId: val.accountId, displayName: val.displayName } } : { assignee: null };
      case 'reporter':
        return val ? { reporter: { accountId: val.accountId, displayName: val.displayName } } : { reporter: null };
      case 'labels':
        return { labels: Array.isArray(val) ? val : [] };
      case 'components':
        return { components: Array.isArray(val) ? val.map(v => ({ id: v.id, name: v.name })) : [] };
      case 'fixVersions':
        return { fixVersions: Array.isArray(val) ? val.map(v => ({ id: v.id, name: v.name })) : [] };
      case 'timeoriginalestimate':
        return { timeoriginalestimate: val || null };
      case 'timeestimate':
        return { timeestimate: val || null };
      default:
        if (fieldId?.startsWith('customfield_')) {
          if (editorType === 'select') {
            return { [fieldId]: val ? { id: val.id, value: val.value || val.name } : null };
          }
          if (editorType === 'multiselect') {
            return { [fieldId]: Array.isArray(val) ? val : [] };
          }
          if (editorType === 'date') {
            // Mirror formatForApi: keep local state as null when the
            // user clears the date, so anything reading the field
            // (formula columns, gantt bars, the screen panel) sees a
            // cleared state instead of an empty string.
            return { [fieldId]: val || null };
          }
          return { [fieldId]: val };
        }
        return { [fieldId]: val };
    }
  };

  const handleSave = async (overrideVal) => {
    const val = overrideVal !== undefined ? overrideVal : editValue;
    onSaving?.();
    setSaving(true);
    setError(null);

    try {
      if (editorType === 'parent') {
        // Parent editor: delegate all API work to the parent save handler
        onSave(fieldId, val);
        return;
      } else if (editorType === 'status') {
        await api.transitionIssue(issueKey, val.id);
        const localUpdate = {
          status: {
            name: val.to?.name || val.name,
            statusCategory: val.to?.statusCategory,
          }
        };
        setSavingTransitionId(null);
        onSave(fieldId, localUpdate);
      } else {
        const apiFields = formatForApi(val);
        await api.updateIssue(issueKey, apiFields);
        const localUpdate = buildLocalUpdate(val);
        onSave(fieldId, localUpdate);
      }
    } catch (err) {
      setSavingTransitionId(null);
      setError(extractErrorMessage(err, 'Failed to update field'));
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSave();
    }
  };

  // Auto-save callback for select/user/date editors
  const handleAutoSave = (val) => {
    handleSave(val);
  };

  // Backdrop click: save-on-close types save, auto-save types just close
  const handleBackdropClick = (e) => {
    if (e.target !== e.currentTarget) return;
    if (isSaveOnClose && !saving) {
      handleSave();
    } else {
      onClose();
    }
  };

  // Close button: same behavior as backdrop click
  const handleCloseClick = () => {
    if (isSaveOnClose && !saving) {
      handleSave();
    } else {
      onClose();
    }
  };

  // Status auto-save on selection — close immediately, transition happens in background
  const handleStatusSelect = async (transition) => {
    // Screen transition: open the transition-screen modal so the user can fill
    // the screen's fields + comment, instead of firing immediately.
    if (transition.hasScreen) {
      setScreenTransition(transition);
      return;
    }
    onClose(); // Close dropdown immediately
    onSaving?.(issueKey, fieldId, transition); // Notify parent to show transitioning state
    try {
      await api.transitionIssue(issueKey, transition.id);
      const localUpdate = {
        status: {
          name: transition.to?.name || transition.name,
          statusCategory: transition.to?.statusCategory,
        }
      };
      onSave(fieldId, localUpdate, issueKey); // Pass issueKey explicitly since editingField may be null
    } catch (err) {
      console.error('Transition failed:', err);
      const errorMsg = extractErrorMessage(err, 'Transition failed');
      onSave(fieldId, null, issueKey, errorMsg); // Pass error so parent can show it
    }
  };

  // Submit a screen transition with the modal's collected fields + comment.
  // Resolves on success (updates the cell + closes); throws the failure so the
  // modal shows the reason and stays open.
  const submitScreenTransition = async (fields, comment) => {
    const transition = screenTransition;
    try {
      await api.transitionIssue(issueKey, transition.id, null, fields, comment);
      const localUpdate = {
        status: {
          name: transition.to?.name || transition.name,
          statusCategory: transition.to?.statusCategory,
        }
      };
      setScreenTransition(null);
      onSave(fieldId, localUpdate, issueKey);
      onClose();
    } catch (err) {
      console.error('Transition failed:', err);
      const data = err?.response?.data;
      const reasons = Array.isArray(data?.errorMessages) ? data.errorMessages.filter(Boolean) : [];
      throw { ok: false, error: (data && data.error) || extractErrorMessage(err, 'Transition failed'), reasons };
    }
  };

  const renderEditor = () => {
    switch (editorType) {
      case 'text':
        return (
          <TextEditor
            value={editValue}
            onChange={setEditValue}
            disabled={saving}
            onKeyDown={handleKeyDown}
          />
        );
      case 'date':
        return (
          <DateEditor
            value={editValue}
            disabled={saving}
            onAutoSave={handleAutoSave}
          />
        );
      case 'number':
        return (
          <NumberEditor
            value={editValue}
            onChange={setEditValue}
            disabled={saving}
            onKeyDown={handleKeyDown}
          />
        );
      case 'time':
        return (
          <TimeEditor
            value={editValue}
            onChange={setEditValue}
            disabled={saving}
            onKeyDown={handleKeyDown}
          />
        );
      case 'select':
        return (
          <SelectEditor
            value={editValue}
            onChange={setEditValue}
            disabled={saving}
            options={options}
            loading={optionsLoading}
            onAutoSave={handleAutoSave}
          />
        );
      case 'status':
        return (
          <StatusEditor
            value={editValue}
            onChange={handleStatusSelect}
            disabled={saving}
            transitions={transitions}
            loading={optionsLoading}
            savingId={savingTransitionId}
          />
        );
      case 'user':
        return (
          <UserEditor
            value={editValue}
            onChange={setEditValue}
            disabled={saving}
            onSearchUsers={searchUsers}
            onAutoSave={handleAutoSave}
          />
        );
      case 'labels':
        return (
          <LabelsEditor
            value={editValue}
            onChange={setEditValue}
            disabled={saving}
            onFetchLabels={() => api.getLabels()}
          />
        );
      case 'multiselect':
        return (
          <MultiSelectEditor
            value={editValue}
            onChange={setEditValue}
            disabled={saving}
            options={options}
            loading={optionsLoading}
          />
        );
      case 'parent':
        return (
          <ParentEditor
            value={currentValue}
            disabled={saving}
            onAutoSave={handleAutoSave}
            projectKey={parentEditorConfig?.projectKey}
            parentIssueTypes={parentEditorConfig?.parentIssueTypes}
            currentIssueKey={issueKey}
            currentJQL={combineJqlFilters(parentEditorConfig?.relationship?.filters)}
            expression={combineExpressions(parentEditorConfig?.relationship?.filters)}
            anchorKey={issueKey}
          />
        );
      default:
        return (
          <TextEditor
            value={editValue}
            onChange={setEditValue}
            disabled={saving}
            onKeyDown={handleKeyDown}
          />
        );
    }
  };

  if (screenTransition) {
    return (
      <TransitionScreenModal
        issueKey={issueKey}
        transition={screenTransition}
        onClose={() => { setScreenTransition(null); onClose(); }}
        onSubmit={submitScreenTransition}
      />
    );
  }

  return (
    <>
      <div className="field-editor-backdrop" onClick={handleBackdropClick} />
      <div
        className="field-editor-dropdown"
        ref={popoverRef}
        style={getPosition()}
      >
        <button
          className="field-editor-close-btn"
          onClick={handleCloseClick}
          title="Close"
          type="button"
        >
          &times;
        </button>

        <div className={`field-editor-body ${needsPadding ? 'field-editor-body-padded' : ''}`}>
          {renderEditor()}
          {error && <div className="field-editor-error">{error}</div>}
        </div>
      </div>
    </>
  );
}

export default FieldEditor;
